import React, { useState } from 'react';
import { usePower } from '../state/PowerContext';
import { daysElapsed, todayISO } from '../utils/billingPeriod';
import PeakDetailsModal from './PeakDetailsModal';

function fmtValue(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function hourLabel(h) {
  if (h == null) return '';
  const hh = ((h - 1) % 12) + 1;
  const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
  return `${hh}${ampm}`;
}

// One slim row that replaces the two big urgent/status cards.
// Everything the user needed from them, rendered horizontally.
export default function CompactHeader({ high, dangerCount, warningCount, latestSheet, records }) {
  const { activePeriod, series } = usePower();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const elapsed = daysElapsed(activePeriod);
  const total = activePeriod.lengthDays;
  const remaining = total - elapsed;

  let mood = 'ok';
  if (dangerCount > 0) mood = 'danger';
  else if (warningCount > 0) mood = 'warning';

  return (
    <div className={`compact-header ${mood}`}>
      <div className={`dot ${mood}`} />
      <span
        className="chip clickable"
        onClick={() => high && setDetailsOpen(true)}
        title={high ? 'Click for surrounding-hour breakdown' : ''}
        style={{ cursor: high ? 'pointer' : 'default' }}
      >
        <span className="chip-label">Period high {high && 'ⓘ'}</span>
        <span className="chip-val">{fmtValue(high?.value)}</span>
        <span className="chip-sub">{series}</span>
      </span>
      <span
        className="chip clickable"
        onClick={() => high && setDetailsOpen(true)}
        title={high ? 'Click for surrounding-hour breakdown' : ''}
        style={{ cursor: high ? 'pointer' : 'default' }}
      >
        <span className="chip-label">Peak hit</span>
        <span className="chip-val">
          {high ? `${high.date.slice(5)} ${hourLabel(high.hour)}` : '—'}
        </span>
        <span className="chip-sub">{high?.isForecast ? 'forecast' : 'actual'}</span>
      </span>
      {detailsOpen && (
        <PeakDetailsModal peak={high} records={records} onClose={() => setDetailsOpen(false)} />
      )}
      <span className="chip" title={activePeriod.name}>
        <span className="chip-label">Billing group</span>
        <span className="chip-val" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {activePeriod.name}
        </span>
        <span className="chip-sub">
          {activePeriod.billGroup != null ? `grp ${activePeriod.billGroup} · ` : activePeriod.company ? `${activePeriod.company} · ` : ''}
          {activePeriod.startISO.slice(5)} → {activePeriod.endISO.slice(5)}
        </span>
      </span>
      <span className="chip">
        <span className="chip-label">Day</span>
        <span className="chip-val">{elapsed} / {total}</span>
        <span className="chip-sub">{remaining}d left</span>
      </span>
      <span className="chip">
        <span className="chip-label">Alerts ahead</span>
        <span className="chip-val">
          {dangerCount > 0 && <span style={{ color: 'var(--danger)' }}>{dangerCount} danger</span>}
          {dangerCount > 0 && warningCount > 0 && <span style={{ opacity: 0.5 }}> · </span>}
          {warningCount > 0 && <span style={{ color: 'var(--warning)' }}>{warningCount} warn</span>}
          {dangerCount === 0 && warningCount === 0 && <span style={{ color: 'var(--ok)' }}>none flagged</span>}
        </span>
        <span className="chip-sub">
          {dangerCount === 0 && warningCount === 0
            ? 'no hours ≥90% of period high in the published forecast'
            : 'upcoming hours ≥90% of period high'}
        </span>
      </span>
      <span className="chip" style={{ marginLeft: 'auto' }}>
        <span className="chip-label">Today · latest sheet</span>
        <span className="chip-val">{todayISO().slice(5)} · {latestSheet ? latestSheet.slice(5) : '—'}</span>
      </span>
    </div>
  );
}
